import { PageProps, graphql } from 'gatsby'
import React, { FC } from 'react'
import { useI18next, useTranslation } from 'gatsby-plugin-react-i18next'

import { BlogCard } from '../components/Blog/BlogCard'
import { LayoutBlogArticle } from '../components/Layout/LayoutBlogArticle'
import { LayoutBlogIndex } from '../components/Layout/LayoutBlogIndex'
import { SEO } from '../components/seo'

interface MarkdownRemarkFrontmatter {
    title: string
    path: string
    subtitle: string
    lang: string
    thumbnail: string
}

interface MarkdownRemark {
    html: string
    frontmatter: MarkdownRemarkFrontmatter
}

interface Data {
    markdownRemark: MarkdownRemark
    allMarkdownRemark: {
        edges: {
            node: MarkdownRemark
        }[]
    }
}

interface PageContext {
    slug: string
    parentPath: string
}

const getPathDepth = (path: string) => path.split('/').filter(Boolean).length

const blogIndexTemplate: FC<PageProps<Data, PageContext>> = ({
    data,
    pageContext,
}) => {
    const { markdownRemark, allMarkdownRemark } = data
    const { frontmatter, html } = markdownRemark
    const { t } = useTranslation()

    const parentPath = pageContext.parentPath
    const parentDepth = getPathDepth(parentPath)

    const relatedMarkdownFiles = allMarkdownRemark.edges.filter(({ node }) => {
        const nodePath = node.frontmatter.path
        const nodeDepth = getPathDepth(nodePath)
        if (parentDepth + 2 < 4) {
            return (
                nodePath.startsWith(parentPath) && nodeDepth === parentDepth + 2
            )
        } else {
            return (
                nodePath.startsWith(pageContext.slug) &&
                nodeDepth === parentDepth + 2
            )
        }
    })
    return (
        <LayoutBlogIndex title={t(frontmatter.title)} linkBack={parentPath}>
            <div
                dangerouslySetInnerHTML={{ __html: html }}
                className="md-component"
            />
            <div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-8 w-full">
                    {relatedMarkdownFiles.map(({ node }, i) => {
                        const { title, path, subtitle, thumbnail } =
                            node.frontmatter
                        return (
                            <BlogCard
                                picture={thumbnail}
                                key={i}
                                title={title}
                                path={path}
                                description={subtitle}
                            />
                        )
                    })}
                </div>
            </div>
        </LayoutBlogIndex>
    )
}

export const Head = () => {
    const { t } = useI18next()

    return <SEO />
}

export const combinedQuery = graphql`
    query ($slug: String!, $language: String!) {
        markdownRemark(
            frontmatter: { path: { eq: $slug }, lang: { eq: $language } }
        ) {
            html
            frontmatter {
                title
                lang
                path
            }
        }
        allMarkdownRemark(
            filter: { frontmatter: { lang: { eq: $language } } }
        ) {
            edges {
                node {
                    frontmatter {
                        title
                        path
                        subtitle
                        lang
                        thumbnail
                    }
                    fields {
                        relativePath
                    }
                }
            }
        }
        locales: allLocale(filter: { language: { eq: $language } }) {
            edges {
                node {
                    ns
                    data
                    language
                }
            }
        }
    }
`

export default blogIndexTemplate
